import { useCallback, useContext, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { IconChevronDownOutline18 as ChevronDown } from "nucleo-ui-outline-18"
import { v4 as uuidv4 } from "uuid"

import type {
	SlaCondition,
	SlaConditionField,
	SlaConditionGroup,
	SlaConditionOperator,
} from "@productlane/lib/src/sla-conditions"
import type { SlaPolicy } from "@productlane/zero"
import { scale } from "@productlane/lib"
import { cn } from "@productlane/lib/src/cn"
import {
	OPERATOR_LABELS,
	SLA_CONDITION_FIELDS,
} from "@productlane/lib/src/sla-conditions"
import {
	AlertDialog,
	Button,
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	Switch,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { SlaTimePicker } from "@/components/sla-time-picker"
import { toast } from "@/components/toast"
import { UpgradeButton } from "@/components/upgrade-button"
import { formatMinutesAsLabel } from "@/lib/dates"
import { useRequiredPlans } from "@/lib/plans"
import { useSession } from "@/lib/session"
import { useZero, useZeroWorkspace } from "@/lib/zero"
import { SettingItem } from "../settings-switches/setting-item"
import {
	SETTINGS_INSET_CLASS,
	SettingsCardInsetContext,
	SettingsContainer,
} from "../settings-switches/settings-container"

// ── Default policies created when SLAs are first enabled ─────────────────────

interface DefaultPolicy {
	name: string
	conditions: SlaConditionGroup | null
	firstResponseMinutes: number | null
	nextResponseMinutes: number | null
	timeToCloseMinutes: number | null
}

const DEFAULT_POLICIES: DefaultPolicy[] = [
	{
		name: "High Priority",
		conditions: {
			match: "all",
			conditions: [
				{ field: "thread.painLevel", operator: "eq", value: "HIGH" },
			],
		},
		firstResponseMinutes: 60,
		nextResponseMinutes: 240,
		timeToCloseMinutes: 1440,
	},
	{
		name: "Medium Priority",
		conditions: {
			match: "all",
			conditions: [
				{ field: "thread.painLevel", operator: "eq", value: "MEDIUM" },
			],
		},
		firstResponseMinutes: 240,
		nextResponseMinutes: 480,
		timeToCloseMinutes: 2880,
	},
	{
		name: "Low Priority",
		conditions: {
			match: "all",
			conditions: [{ field: "thread.painLevel", operator: "eq", value: "LOW" }],
		},
		firstResponseMinutes: 480,
		nextResponseMinutes: 1440,
		timeToCloseMinutes: 10080,
	},
	{
		name: "Default",
		conditions: null,
		firstResponseMinutes: null,
		nextResponseMinutes: null,
		timeToCloseMinutes: null,
	},
]

// ── Helpers ──────────────────────────────────────────────────────────────────

const FIELD_ENTRIES = Object.entries(SLA_CONDITION_FIELDS) as [
	SlaConditionField,
	(typeof SLA_CONDITION_FIELDS)[SlaConditionField],
][]

const FIELD_GROUPS = ["Thread", "Company", "Contact"] as const

function getDefaultValueForField(field: SlaConditionField): string | number {
	const meta = SLA_CONDITION_FIELDS[field]
	if (meta.options?.length) return meta.options[0]!.value
	if (meta.type === "number") return 0
	return ""
}

const operatorLabel = (
	field: SlaConditionField,
	operator: SlaConditionOperator,
) =>
	SLA_CONDITION_FIELDS[field].operatorLabels?.[operator] ??
	OPERATOR_LABELS[operator]

function createDefaultCondition(): SlaCondition {
	const [fieldKey, fieldMeta] = FIELD_ENTRIES[0]!
	return {
		field: fieldKey,
		operator: fieldMeta.operators[0]!,
		value: getDefaultValueForField(fieldKey),
	}
}

function createDefaultConditionGroup(): SlaConditionGroup {
	return { match: "all", conditions: [createDefaultCondition()] }
}

function formatPolicySummary(policy: SlaPolicy): string {
	const conditions = policy.conditions as SlaConditionGroup | null
	if (!conditions) return "All threads"
	const count = conditions.conditions.length
	if (count === 0) return "All threads"
	return `${count} condition${count > 1 ? "s" : ""} · ${conditions.match.toUpperCase()}`
}

function formatTimeSummary(policy: SlaPolicy): string {
	const parts = [
		formatMinutesAsLabel(policy.firstResponseMinutes),
		formatMinutesAsLabel(policy.nextResponseMinutes),
		formatMinutesAsLabel(policy.timeToCloseMinutes),
	]
	return parts.join(" / ")
}

// ── Main component ───────────────────────────────────────────────────────────

export function SlaSection() {
	const workspace = useZeroWorkspace()
	const zero = useZero()
	const { session } = useSession()

	const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
	const { passes: hasRequiredPlan } = useRequiredPlans(scale.andUp)
	const inset = useContext(SettingsCardInsetContext)

	const toggleExpand = useCallback((id: string) => {
		setExpandedIds((prev) => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	}, [])

	const sortedPolicies = useMemo(() => {
		if (!workspace?.slaPolicies) return []
		return [...workspace.slaPolicies].sort((a, b) => a.position - b.position)
	}, [workspace?.slaPolicies])

	const handleToggleBusinessHours = useCallback(
		(enabled: boolean) => {
			if (!session?.workspaceId) return
			zero.mutate(
				mutators.workspaces.update({
					id: session.workspaceId,
					slaUseBusinessHours: enabled,
				}),
			)
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[session?.workspaceId, zero.mutate],
	)

	const handleToggleSla = useCallback(
		(enabled: boolean) => {
			if (!session?.workspaceId) return

			zero.mutate(
				mutators.workspaces.update({
					id: session.workspaceId,
					slaEnabled: enabled,
				}),
			)

			if (enabled && sortedPolicies.length === 0) {
				for (let i = 0; i < DEFAULT_POLICIES.length; i++) {
					const def = DEFAULT_POLICIES[i]!
					zero.mutate(
						mutators.slaPolicies.create({
							id: uuidv4(),
							name: def.name,
							conditions: def.conditions,
							position: i,
							firstResponseMinutes: def.firstResponseMinutes,
							nextResponseMinutes: def.nextResponseMinutes,
							timeToCloseMinutes: def.timeToCloseMinutes,
						}),
					)
				}
			}

			toast.success({ title: enabled ? "SLAs enabled" : "SLAs disabled" })
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[session?.workspaceId, zero.mutate, sortedPolicies.length],
	)

	const handleAddPolicy = useCallback(() => {
		const id = uuidv4()
		zero.mutate(
			mutators.slaPolicies.create({
				id,
				name: "New Policy",
				conditions: createDefaultConditionGroup(),
				position: sortedPolicies.length,
				firstResponseMinutes: null,
				nextResponseMinutes: null,
				timeToCloseMinutes: null,
			}),
		)
		setExpandedIds((prev) => new Set(prev).add(id))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [zero.mutate, sortedPolicies.length])

	const handleDeletePolicy = useCallback(
		(id: string) => {
			zero.mutate(mutators.slaPolicies.delete({ id }))
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[zero.mutate],
	)

	const handleMovePolicy = useCallback(
		(index: number, direction: "up" | "down") => {
			const swapIndex = direction === "up" ? index - 1 : index + 1
			if (swapIndex < 0 || swapIndex >= sortedPolicies.length) return
			const ids = sortedPolicies.map((p) => p.id)
			;[ids[index], ids[swapIndex]] = [ids[swapIndex]!, ids[index]!]
			zero.mutate(mutators.slaPolicies.reorder({ policyIds: ids }))
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[zero.mutate, sortedPolicies],
	)

	const handleUpdatePolicy = useCallback(
		(
			policyId: string,
			field:
				| "name"
				| "firstResponseMinutes"
				| "nextResponseMinutes"
				| "timeToCloseMinutes",
			value: string | number | null,
		) => {
			zero.mutate(
				mutators.slaPolicies.update({
					id: policyId,
					[field]: value,
				}),
			)
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[zero.mutate],
	)

	const handleUpdateConditions = useCallback(
		(policyId: string, conditions: SlaConditionGroup | null) => {
			zero.mutate(
				mutators.slaPolicies.update({
					id: policyId,
					conditions,
				}),
			)
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[zero.mutate],
	)

	if (!workspace) return null

	const isEnabled = workspace.slaEnabled

	return (
		<div className="w-full space-y-6">
			<SettingsContainer>
				<SettingItem
					title="Enable SLAs"
					description="Track response time targets for support threads."
					action={
						<>
							{hasRequiredPlan ? (
								<Switch
									name="sla-enabled"
									checked={isEnabled}
									onCheckedChange={handleToggleSla}
								/>
							) : (
								<UpgradeButton required={scale.andUp} />
							)}
						</>
					}
				/>
				{isEnabled && hasRequiredPlan && (
					<SettingItem
						title="Use office hours"
						className="pt-3"
						description="Only count time during office hours for automatically applied SLAs."
						action={
							<Switch
								name="sla-use-business-hours"
								checked={workspace.slaUseBusinessHours}
								onCheckedChange={handleToggleBusinessHours}
							/>
						}
					/>
				)}
			</SettingsContainer>

			{isEnabled && hasRequiredPlan && (
				<div className={cn("space-y-3", inset && SETTINGS_INSET_CLASS)}>
					<div className="flex items-center justify-between">
						<p className="text-sm text-tertiary sm:ml-3">
							Policies are evaluated top to bottom. The first matching policy
							applies.
						</p>
						<Button size="sm" variant="outline" onClick={handleAddPolicy}>
							Add policy
						</Button>
					</div>

					{sortedPolicies.map((policy, index) => (
						<SlaPolicyCard
							key={policy.id}
							policy={policy}
							index={index}
							total={sortedPolicies.length}
							isExpanded={expandedIds.has(policy.id)}
							onToggleExpand={() => toggleExpand(policy.id)}
							onMove={handleMovePolicy}
							onDelete={handleDeletePolicy}
							onUpdateField={handleUpdatePolicy}
							onUpdateConditions={handleUpdateConditions}
						/>
					))}
				</div>
			)}
		</div>
	)
}

// ── Policy card ──────────────────────────────────────────────────────────────

function SlaPolicyCard({
	policy,
	index,
	total,
	isExpanded,
	onToggleExpand,
	onMove,
	onDelete,
	onUpdateField,
	onUpdateConditions,
}: {
	policy: SlaPolicy
	index: number
	total: number
	isExpanded: boolean
	onToggleExpand: () => void
	onMove: (index: number, direction: "up" | "down") => void
	onDelete: (id: string) => void
	onUpdateField: (
		id: string,
		field:
			| "name"
			| "firstResponseMinutes"
			| "nextResponseMinutes"
			| "timeToCloseMinutes",
		value: string | number | null,
	) => void
	onUpdateConditions: (id: string, conditions: SlaConditionGroup | null) => void
}) {
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
	const conditions = policy.conditions as SlaConditionGroup | null
	const isCatchAll = !conditions

	// ── Collapsed view ──────────────────────────────────────────────────
	if (!isExpanded) {
		return (
			<div className="w-full rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-850">
				<div className="flex items-center gap-3 px-4 py-3">
					<MoveButtons index={index} total={total} onMove={onMove} />

					<div className="min-w-0 flex-1">
						<span className="text-sm font-medium">{policy.name}</span>
						<div className="flex items-center gap-2 text-xs text-tertiary">
							<span>{formatPolicySummary(policy)}</span>
							<span>·</span>
							<span>{formatTimeSummary(policy)}</span>
						</div>
					</div>

					<Button size="sm" variant="outline" onClick={onToggleExpand}>
						Edit
					</Button>
				</div>
			</div>
		)
	}

	// ── Expanded view ───────────────────────────────────────────────────
	return (
		<div className="w-full rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-850">
			{/* Header */}
			<div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-700">
				<MoveButtons index={index} total={total} onMove={onMove} />

				<input
					className="flex-1 bg-transparent text-sm font-medium outline-none placeholder:text-tertiary"
					value={policy.name}
					onChange={(e) => onUpdateField(policy.id, "name", e.target.value)}
					placeholder="Policy name"
				/>

				<div className="inline-flex flex-shrink-0 rounded-lg border border-gray-300/70 bg-gray-100 p-0.5 dark:border-gray-700/40 dark:bg-gray-900">
					{(
						[
							{ value: "catchAll", label: "All threads" },
							{ value: "conditions", label: "Conditions" },
						] as const
					).map((option) => {
						const isActive =
							option.value === "catchAll" ? isCatchAll : !isCatchAll
						return (
							<button
								key={option.value}
								type="button"
								className={cn(
									"w-[80px] rounded-[0.45rem] py-1 text-center text-xs transition-colors",
									isActive
										? "bg-white text-primary shadow-sm ring-1 ring-gray-400/60 dark:bg-gray-800 dark:ring-gray-700"
										: "text-secondary hover:text-primary",
								)}
								onClick={() =>
									onUpdateConditions(
										policy.id,
										option.value === "conditions"
											? createDefaultConditionGroup()
											: null,
									)
								}
							>
								{option.label}
							</button>
						)
					})}
				</div>

				<button
					type="button"
					className="text-tertiary hover:text-red-500"
					onClick={() => setConfirmDeleteOpen(true)}
					title="Delete policy"
				>
					<svg
						width="14"
						height="14"
						viewBox="0 0 14 14"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
					>
						<path d="M3 3L11 11M3 11L11 3" />
					</svg>
				</button>
			</div>

			<AlertDialog
				isOpen={confirmDeleteOpen}
				onOpenChange={setConfirmDeleteOpen}
				title={`Delete "${policy.name}"?`}
				description="This policy will be permanently removed. This action cannot be undone."
				confirmText="Delete"
				onCancel={() => setConfirmDeleteOpen(false)}
				onConfirm={() => onDelete(policy.id)}
			/>

			{/* Conditions */}
			{!isCatchAll && (
				<div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
					<SlaConditionBuilder
						group={conditions}
						onChange={(g) => onUpdateConditions(policy.id, g)}
					/>
				</div>
			)}

			{/* Response times */}
			<div className="flex items-center gap-4 border-b border-gray-100 px-4 py-3 dark:border-gray-700">
				<div className="flex-1">
					<span className="mb-1 block text-xs text-tertiary">
						First Response
					</span>
					<SlaTimePicker
						value={policy.firstResponseMinutes}
						onChange={(val) =>
							onUpdateField(policy.id, "firstResponseMinutes", val)
						}
					/>
				</div>
				<div className="flex-1">
					<span className="mb-1 block text-xs text-tertiary">
						Next Response
					</span>
					<SlaTimePicker
						value={policy.nextResponseMinutes}
						onChange={(val) =>
							onUpdateField(policy.id, "nextResponseMinutes", val)
						}
					/>
				</div>
				<div className="flex-1">
					<span className="mb-1 block text-xs text-tertiary">
						Time to Close
					</span>
					<SlaTimePicker
						value={policy.timeToCloseMinutes}
						onChange={(val) =>
							onUpdateField(policy.id, "timeToCloseMinutes", val)
						}
					/>
				</div>
			</div>

			{/* Footer with Save */}
			<div className="flex justify-end px-4 py-2">
				<Button size="sm" variant="default" onClick={onToggleExpand}>
					Save
				</Button>
			</div>
		</div>
	)
}

// ── Move up/down buttons ─────────────────────────────────────────────────────

function MoveButtons({
	index,
	total,
	onMove,
}: {
	index: number
	total: number
	onMove: (index: number, direction: "up" | "down") => void
}) {
	return (
		<div className="flex flex-col">
			<button
				type="button"
				className="text-tertiary hover:text-primary disabled:opacity-30"
				disabled={index === 0}
				onClick={() => onMove(index, "up")}
				title="Move up"
			>
				<svg
					width="12"
					height="12"
					viewBox="0 0 12 12"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
				>
					<path d="M2 8L6 4L10 8" />
				</svg>
			</button>
			<button
				type="button"
				className="text-tertiary hover:text-primary disabled:opacity-30"
				disabled={index === total - 1}
				onClick={() => onMove(index, "down")}
				title="Move down"
			>
				<svg
					width="12"
					height="12"
					viewBox="0 0 12 12"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
				>
					<path d="M2 4L6 8L10 4" />
				</svg>
			</button>
		</div>
	)
}

// ── Condition builder ────────────────────────────────────────────────────────

function SlaConditionBuilder({
	group,
	onChange,
}: {
	group: SlaConditionGroup
	onChange: (group: SlaConditionGroup) => void
}) {
	const handleMatchChange = (match: "all" | "any") => {
		onChange({ ...group, match })
	}

	const handleAddCondition = () => {
		const firstField = FIELD_ENTRIES[0]!
		const [fieldKey, fieldMeta] = firstField
		onChange({
			...group,
			conditions: [
				...group.conditions,
				{
					field: fieldKey,
					operator: fieldMeta.operators[0]!,
					value: getDefaultValueForField(fieldKey),
				},
			],
		})
	}

	const handleUpdateCondition = (index: number, condition: SlaCondition) => {
		const updated = [...group.conditions]
		updated[index] = condition
		onChange({ ...group, conditions: updated })
	}

	const handleRemoveCondition = (index: number) => {
		const updated = group.conditions.filter((_, i) => i !== index)
		onChange({ ...group, conditions: updated })
	}

	return (
		<div className="flex gap-3">
			{/* Toggle + vertical group line */}
			<div className="flex flex-col items-center">
				<div className="inline-flex flex-col rounded-lg border border-gray-300/70 bg-gray-100 p-0.5 dark:border-gray-700/40 dark:bg-gray-900">
					{(
						[
							{ value: "all", label: "ALL" },
							{ value: "any", label: "ANY" },
						] as const
					).map((option) => (
						<button
							key={option.value}
							type="button"
							className={cn(
								"rounded-[0.45rem] px-2.5 py-1 text-xs font-medium transition-colors",
								group.match === option.value
									? "bg-white text-primary shadow-sm ring-1 ring-gray-400/60 dark:bg-gray-800 dark:ring-gray-700"
									: "text-secondary hover:text-primary",
							)}
							onClick={() => handleMatchChange(option.value)}
						>
							{option.label}
						</button>
					))}
				</div>
				{group.conditions.length > 0 && (
					<div className="mt-1 w-px flex-1 bg-gray-200 dark:bg-gray-700" />
				)}
			</div>

			{/* Condition rows */}
			<div className="flex-1 space-y-2 pt-1">
				{group.conditions.map((condition, index) => (
					<SlaConditionRow
						key={index}
						condition={condition}
						onChange={(c) => handleUpdateCondition(index, c)}
						onRemove={() => handleRemoveCondition(index)}
					/>
				))}

				<Button size="sm" variant="dashed" onClick={handleAddCondition}>
					+ Add condition
				</Button>

				{group.conditions.length === 0 && (
					<p className="text-xs text-tertiary">
						No conditions — matches all threads.
					</p>
				)}
			</div>
		</div>
	)
}

// ── Condition row ────────────────────────────────────────────────────────────

function SlaConditionRow({
	condition,
	onChange,
	onRemove,
}: {
	condition: SlaCondition
	onChange: (condition: SlaCondition) => void
	onRemove: () => void
}) {
	const fieldMeta = SLA_CONDITION_FIELDS[condition.field]

	const handleFieldChange = (field: SlaConditionField) => {
		const meta = SLA_CONDITION_FIELDS[field]
		onChange({
			field,
			operator: meta.operators[0]!,
			value: getDefaultValueForField(field),
		})
	}

	const handleOperatorChange = (operator: SlaConditionOperator) => {
		onChange({ ...condition, operator })
	}

	const handleValueChange = (value: string | number) => {
		onChange({ ...condition, value })
	}

	return (
		<div className="flex w-full items-center gap-1.5">
			{/* Field selector */}
			<ConditionDropdown label={fieldMeta.label} className="w-[130px]">
				{FIELD_GROUPS.map((group, gi) => (
					<span key={group}>
						{gi > 0 && <DropdownMenuSeparator />}
						<DropdownMenuLabel>{group}</DropdownMenuLabel>
						<DropdownMenuGroup>
							{FIELD_ENTRIES.filter(([, m]) => m.group === group).map(
								([key, m]) => (
									<DropdownMenuItem
										key={key}
										onClick={() => handleFieldChange(key)}
									>
										<span
											className={cn(condition.field === key && "font-semibold")}
										>
											{m.label}
										</span>
									</DropdownMenuItem>
								),
							)}
						</DropdownMenuGroup>
					</span>
				))}
			</ConditionDropdown>

			{/* Operator selector */}
			<ConditionDropdown
				label={operatorLabel(condition.field, condition.operator)}
				className="w-[150px]"
			>
				<DropdownMenuGroup>
					{fieldMeta.operators.map((op) => (
						<DropdownMenuItem key={op} onClick={() => handleOperatorChange(op)}>
							<span
								className={cn(condition.operator === op && "font-semibold")}
							>
								{operatorLabel(condition.field, op)}
							</span>
						</DropdownMenuItem>
					))}
				</DropdownMenuGroup>
			</ConditionDropdown>

			{/* Value input */}
			<ConditionValueInput
				field={condition.field}
				value={condition.value}
				onChange={handleValueChange}
			/>

			{/* Remove button */}
			<button
				type="button"
				className="flex-shrink-0 text-tertiary hover:text-red-500"
				onClick={onRemove}
				title="Remove condition"
			>
				<svg
					width="14"
					height="14"
					viewBox="0 0 14 14"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
				>
					<path d="M3 3L11 11M3 11L11 3" />
				</svg>
			</button>
		</div>
	)
}

// ── Styled dropdown trigger ──────────────────────────────────────────────────

function ConditionDropdown({
	label,
	children,
	className,
	disabled = false,
}: {
	label: string
	disabled?: boolean
	children: React.ReactNode
	className?: string
}) {
	if (disabled) {
		return (
			<span
				className={cn(
					"inline-flex items-center gap-1 truncate rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs text-secondary dark:border-gray-700 dark:bg-gray-800",
					className,
				)}
			>
				{label}
			</span>
		)
	}

	return (
		<DropdownMenu
			trigger={
				<button
					type="button"
					className={cn(
						"inline-flex items-center justify-between gap-1 truncate rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-850 dark:hover:bg-gray-800",
						className,
					)}
				>
					<span className="truncate">{label}</span>
					<ChevronDown className="h-3 w-3 flex-shrink-0 text-tertiary" />
				</button>
			}
			contentProps={{ side: "bottom", align: "start" }}
		>
			{children}
		</DropdownMenu>
	)
}

// ── Value input (adapts to field type) ───────────────────────────────────────

function ConditionValueInput({
	field,
	value,
	onChange,
}: {
	field: SlaConditionField
	value: string | number | null
	onChange: (value: string | number) => void
}) {
	const meta = SLA_CONDITION_FIELDS[field]

	if (meta.type === "tag") {
		return <TagValueInput value={value} onChange={onChange} />
	}

	if (meta.options) {
		const selectedLabel =
			meta.options.find((o) => o.value === String(value))?.label ??
			String(value ?? "")

		return (
			<ConditionDropdown label={selectedLabel} className="min-w-[120px] flex-1">
				<DropdownMenuGroup>
					{meta.options.map((opt) => (
						<DropdownMenuItem
							key={opt.value}
							onClick={() => onChange(opt.value)}
						>
							<span
								className={cn(String(value) === opt.value && "font-semibold")}
							>
								{opt.label}
							</span>
						</DropdownMenuItem>
					))}
				</DropdownMenuGroup>
			</ConditionDropdown>
		)
	}

	if (meta.type === "number") {
		return (
			<input
				type="number"
				className="min-w-[120px] flex-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-primary outline-none transition-colors focus:border-blue-400 focus:ring-1 focus:ring-blue-400 dark:border-gray-700 dark:bg-gray-850 dark:focus:border-blue-500"
				value={value ?? ""}
				onChange={(e) => onChange(e.target.value ? Number(e.target.value) : 0)}
				placeholder="Value"
			/>
		)
	}

	return (
		<input
			type="text"
			className="min-w-[120px] flex-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-primary outline-none transition-colors focus:border-blue-400 focus:ring-1 focus:ring-blue-400 dark:border-gray-700 dark:bg-gray-850 dark:focus:border-blue-500"
			value={String(value ?? "")}
			onChange={(e) => onChange(e.target.value)}
			placeholder="Value"
		/>
	)
}

function TagValueInput({
	value,
	onChange,
}: {
	value: string | number | null
	onChange: (value: string) => void
}) {
	const [tags] = useQuery(queries.tags())
	const selected = tags.find((tag) => tag.id === value)

	return (
		<ConditionDropdown
			label={selected?.name ?? "Select tag"}
			className="min-w-[120px] flex-1"
		>
			<DropdownMenuGroup className="max-h-72 overflow-y-auto">
				{tags.map((tag) => (
					<DropdownMenuItem key={tag.id} onClick={() => onChange(tag.id)}>
						<span className={cn(tag.id === value && "font-semibold")}>
							{tag.name}
						</span>
					</DropdownMenuItem>
				))}
			</DropdownMenuGroup>
		</ConditionDropdown>
	)
}
